/**
 * ConfigManager - Manages application configuration and user preferences
 */

import { Logger } from './Logger';
import { userSettingsService, SettingScope } from './UserSettingsService';

const logger = Logger.scope('ConfigManager');

export interface AppConfig {
  showFormBorder: boolean;
  // Add more config options here as needed
}

/**
 * Configuration manager for storing and retrieving user preferences
 */
export class ConfigManager {
  private static readonly STORAGE_KEY = 'app-config';
  private static readonly DEFAULT_CONFIG: AppConfig = {
    showFormBorder: true,
  };

  private config: AppConfig;

  constructor() {
    this.config = this.load();
  }

  /**
   * Get current configuration
   */
  public getConfig(): Readonly<AppConfig> {
    return { ...this.config };
  }

  /**
   * Get a specific config value
   */
  public get<K extends keyof AppConfig>(key: K): AppConfig[K] {
    return this.config[key];
  }

  /**
   * Set a specific config value
   */
  public set<K extends keyof AppConfig>(key: K, value: AppConfig[K]): void {
    this.config[key] = value;
    this.save();
    this.apply();
  }

  /**
   * Update multiple config values at once
   */
  public update(updates: Partial<AppConfig>): void {
    this.config = { ...this.config, ...updates };
    this.save();
    this.apply();
  }

  /**
   * Reset configuration to defaults
   */
  public reset(): void {
    this.config = { ...ConfigManager.DEFAULT_CONFIG };
    this.save();
    this.apply();
  }

  /**
   * Load configuration from UserSettingsService
   */
  private load(): AppConfig {
    try {
      const stored = userSettingsService.get<AppConfig>(ConfigManager.STORAGE_KEY);
      if (stored) {
        return { ...ConfigManager.DEFAULT_CONFIG, ...stored };
      }
    } catch (error) {
      logger.warn('Failed to load config:', error);
    }
    return { ...ConfigManager.DEFAULT_CONFIG };
  }

  /**
   * Save configuration to UserSettingsService
   */
  private save(): void {
    // Fire and forget
    userSettingsService.set(ConfigManager.STORAGE_KEY, this.config, SettingScope.LOCAL).catch(error => {
      logger.warn('Failed to save config:', error);
    });
  }

  /**
   * Apply configuration to the DOM
   */
  private apply(): void {
    logger.debug('Applying config:', this.config);
    
    // Apply showFormBorder setting
    if (this.config.showFormBorder) {
      document.body.classList.remove('hide-form-border');
    } else {
      document.body.classList.add('hide-form-border');
    }

    // Add more config applications here as needed
  }

  /**
   * Initialize configuration (apply settings to DOM)
   */
  public init(): void {
    this.apply();
  }
}

/**
 * Singleton instance for convenient access
 */
export const configManager = new ConfigManager();
